import { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import ServicesTable from '../components/services/ServicesTable';
import ServiceComposeEntry from '../components/services/ServiceComposeEntry';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import { EmptyState } from '../components/shared/EmptyState';
import HowItWorksStrip, { HOW_IT_WORKS_PANEL_KEY } from '../components/shared/HowItWorksStrip';
import { useEndpoints } from '../hooks/useEndpoints';
import { useDismissiblePanel } from '../contexts/UIPreferencesContext';
import { useFlag } from '../hooks/useFeatureFlags';

// Destination for the "New AI Service" button — the compose-based creation
// entry (task 392, Mockup G): describe the service in chat, the composer
// designs and builds it.
const NEW_SERVICE_PATH = '/services/new';

const DEFAULT_LIMIT = 20;

export default function ServicesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  // Search is persisted in the URL (/services?search=…) so results survive
  // back/refresh/share. The SearchBar debounces (~300ms) before calling this.
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set('search', value);
          } else {
            next.delete('search');
          }
          return next;
        },
        { replace: true }
      );
      setPage(1); // Reset to first page on search change
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  // First-contact explainer (task 526): the three-step strip rides above the
  // first-run compose panel until the User dismisses it.
  const howItWorks = useDismissiblePanel(HOW_IT_WORKS_PANEL_KEY);

  const { data, isLoading, error } = useEndpoints(page, limit, {
    search: search || undefined,
  });
  const endpoints = data?.data || [];
  const pagination = data?.pagination;

  // Both ways in to /services/new follow that route's own flag (task 1240),
  // so neither leads to a redirect when the page is off.
  const newServiceEnabled = useFlag('innovate.fe.services.new.page');

  const actions = newServiceEnabled ? (
    <Link
      to={NEW_SERVICE_PATH}
      data-testid="services-new-service"
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
    >
      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
      New AI Service
    </Link>
  ) : undefined;

  const emptyState = (
    <EmptyState
      icon={<MagnifyingGlassIcon className="h-12 w-12" />}
      title="No matching AI Services"
      description={`No AI Services found matching "${search}". Try a different search term.`}
      action={{
        label: 'Clear search',
        onClick: () => handleSearchChange(''),
        variant: 'secondary',
      }}
    />
  );

  // First-run: no services at all (and no active search filtering them out) —
  // the create experience IS the page (task 392, Mockup G), not an empty list.
  const isFirstRun = !isLoading && !error && !search && endpoints.length === 0;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">
          Error loading AI Services. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="AI Services"
        description="Live APIs backed by your AI collaborations — search, open, and manage them here."
        actions={actions}
      />

      {isFirstRun ? (
        // First-run view: describe your first AI Service (Mockup G). Submitting
        // continues at /services/new with the composer conversation seeded.
        <div className="mt-10 max-w-2xl mx-auto">
          {!howItWorks.isDismissed && (
            <div className="mb-6">
              <HowItWorksStrip onDismiss={howItWorks.dismiss} />
            </div>
          )}
          {newServiceEnabled && (
            <ServiceComposeEntry
              onSubmit={(text) => navigate('/services/new', { state: { prompt: text } })}
            />
          )}
        </div>
      ) : (
        <div className="mt-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
            <SearchBar
              value={search}
              onChange={handleSearchChange}
              placeholder="Search AI Services..."
            />
          </div>

          <ServicesTable
            endpoints={endpoints}
            isLoading={isLoading}
            emptyState={emptyState}
          />

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
